import { Box, Paper, Typography } from "@mui/material";
import { useEffect, useMemo, useRef } from "react";

import { AITransparencyLink } from "../../../components/AITransparencyLink";
import { useChatStore } from "../stores/chatStore";
import { ChatMessage as IChatMessage } from "../stores/chatStore";
import ChatMessage from "./ChatMessage";
import RiskWarningBanner from "./RiskWarningBanner";

const NO_ASSISTANT_INDEX = -1;
const EMPTY_MESSAGES = 0;

const styles = {
  container: {
    flexGrow: 1,
    overflow: "auto",
    bgcolor: "#FAFAFA"
  },
  emptyState: {
    p: 3,
    textAlign: "center",
    bgcolor: "#F0F9FF",
    borderRadius: 2
  },
  disclaimer: {
    display: "block",
    ml: 4.5,
    mt: -1,
    mb: 2,
    color: "text.secondary"
  }
} as const;

const EmptyState = (): React.ReactElement => (
  <Paper elevation={0} sx={styles.emptyState}>
    <Typography variant="body1" gutterBottom>
      Ask questions about the filtered data
    </Typography>
    <Typography variant="body2" color="text.secondary">
      You can ask about agent performance, call patterns, risk factors, or request training feedback based on the data.
    </Typography>
  </Paper>
);

interface MessageListProps {
  messages: IChatMessage[];
  viewingPreviousSession: boolean;
  handleEditMessage?: (message: IChatMessage) => void;
  lastAssistantMessageIndex: number;
  shouldShowDisclaimer: boolean;
}

const MessageList = ({
  messages,
  viewingPreviousSession,
  handleEditMessage,
  lastAssistantMessageIndex,
  shouldShowDisclaimer
}: MessageListProps): React.ReactElement => (
  <>
    {messages.map((message: IChatMessage, index: number) => (
      <Box key={message.id}>
        <ChatMessage
          message={message}
          onEdit={viewingPreviousSession ? undefined : handleEditMessage}
        />
        {index === lastAssistantMessageIndex && shouldShowDisclaimer && (
          <Typography
            variant="caption"
            sx={styles.disclaimer}
          >
            AI can make mistakes. Review important details. <AITransparencyLink />
          </Typography>
        )}
      </Box>
    ))}
  </>
);

const ChatMessages = (): React.ReactElement => {
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const {
    messages,
    loading,
    handleEditMessage,
    viewingPreviousSession,
    previousSessionMessages
  } = useChatStore();

  const displayMessages = viewingPreviousSession ? previousSessionMessages : messages;

  const lastAssistantMessageIndex = useMemo(
    () => displayMessages.reduce(
      (lastIndex, message, index) => message.role === "assistant" ? index : lastIndex,
      NO_ASSISTANT_INDEX
    ),
    [displayMessages]
  );

  const shouldShowDisclaimer = useMemo(
    () => lastAssistantMessageIndex !== NO_ASSISTANT_INDEX
      && !displayMessages[lastAssistantMessageIndex]?.isStreaming,
    [displayMessages, lastAssistantMessageIndex]
  );

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [displayMessages]);

  const showEmptyState = displayMessages.length === EMPTY_MESSAGES && !loading;

  return (
    <Box sx={styles.container}>
      <RiskWarningBanner />
      <Box sx={{ p: 2 }}>
        {showEmptyState ? (
          <EmptyState />
        ) : (
          <MessageList
            messages={displayMessages}
            viewingPreviousSession={viewingPreviousSession}
            handleEditMessage={handleEditMessage}
            lastAssistantMessageIndex={lastAssistantMessageIndex}
            shouldShowDisclaimer={shouldShowDisclaimer}
          />
        )}

        <div ref={messagesEndRef} />
      </Box>
    </Box>
  );
};

export default ChatMessages;
